import { useCallback, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';

import { AI_CREDITS_EXHAUSTED, TOO_LARGE_ERROR } from '@flintmint/core-shared/constants';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { capture } from '@/analytics';
import { getUserSourceErrorMessage } from '@/components/context-item';
import { urlOverlays } from '@/constants/url-overlays';
import { isInvited, useOpenAiRefusalInvitation } from '@/features/billing/ai-refusal-invitation';
import { getEnergyView } from '@/features/llm-chat/energy/energy-view';
import { useUserSourcesByIds } from '@/hooks/rxdb-models';
import { useResyncUserSourceById } from '@/hooks/use-resync-user-source';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { useUserAccount } from '@/hooks/use-user-account';
import { processingUploadIdsAtom, userSourceProcessingFailuresAtom, type UserSourceProcessingFailure } from '@/state';

/**
 * Mounted once. Tells the user about every Blueprint processing failure: each one reported to
 * `userSourceProcessingFailuresAtom` (a failed or refused resync), and each upload this client sent for processing
 * that then failed. It records the uploads processing refused for lack of AI Energy, and stops tracking every upload
 * once its processing settles. Queries nothing while no upload is tracked.
 *
 * The query leaves out archived and deleted sources, so an upload that was in the results and is now missing stops
 * being tracked too. One never seen stays tracked: a just-created source can be missing from the first results.
 */
export const UserSourceRefusalWatcher = () => {
  const notifyProcessingFailed = useNotifyProcessingFailed();
  const [failures, setFailures] = useAtom(userSourceProcessingFailuresAtom);
  const [trackedIds, setTrackedIds] = useAtom(processingUploadIdsAtom);
  const { data: sources } = useUserSourcesByIds(trackedIds);
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    if (!failures.length) {
      return;
    }
    for (const failure of failures) {
      notifyProcessingFailed(failure);
    }
    setFailures((current) => current.filter((failure) => !failures.includes(failure)));
  }, [failures, setFailures, notifyProcessingFailed]);

  useEffect(() => {
    const tracked = sources.filter((source) => trackedIds.includes(source.id));
    const settled = tracked.filter((source) => source.syncStatus !== 'processing');
    const gone = trackedIds.filter((id) => seenIds.current.has(id) && !tracked.some((source) => source.id === id));
    for (const source of tracked) seenIds.current.add(source.id);
    if (!settled.length && !gone.length) return;

    for (const source of settled) {
      if (source.syncStatus !== 'error') {
        continue;
      }
      if (source.error === AI_CREDITS_EXHAUSTED) {
        capture('ai_credits_exhausted', { source: 'user_source_upload' });
      }
      notifyProcessingFailed({ userSourceId: source.id, error: source.error, retryable: source.error !== TOO_LARGE_ERROR });
    }
    const droppedIds = [...settled.map((source) => source.id), ...gone];
    for (const id of droppedIds) seenIds.current.delete(id);
    setTrackedIds((ids) => ids.filter((id) => !droppedIds.includes(id)));
  }, [sources, trackedIds, setTrackedIds, notifyProcessingFailed]);

  return null;
};

/**
 * An Energy refusal on an account that can buy a Recharge gets a toast that opens it; on any other account the AI
 * refusal invitation opens instead. Other failures get a toast, with a Resync when resyncing can help. The returned
 * function is stable and runs the latest render's handlers, as do the toast's buttons, so Recharge opens over the page
 * the user is on when they click it.
 */
const useNotifyProcessingFailed = () => {
  const { showToastNotification } = useToaster();
  const { account } = useUserAccount();
  const openAiRefusalInvitation = useOpenAiRefusalInvitation();
  const { open: openRecharge } = useUrlOverlay(urlOverlays.recharge, urlOverlays);
  const resyncById = useResyncUserSourceById();
  const notify = ({ userSourceId, error, retryable }: UserSourceProcessingFailure) => {
    // The refusal itself proves the Energy is gone, so Recharge does not also wait for the account's percentLeft to reach 0.
    if (error === AI_CREDITS_EXHAUSTED && getEnergyView(account)?.action === 'recharge') {
      showToastNotification({
        type: 'error',
        title: 'AI Energy used up',
        description: 'Purchase Joules to recharge, then try processing again.',
        showCloseButton: true,
        action: { label: 'Recharge', onClick: () => latest.current.openRecharge() },
      });
      return;
    }
    // The invitation opens only for an invited account; any other account would get no feedback at all.
    if (error === AI_CREDITS_EXHAUSTED && isInvited(account)) {
      openAiRefusalInvitation();
      return;
    }
    const resync = () => {
      // resyncById reports its own failure or refusal and rethrows; the retry has nothing more to do with it.
      latest.current.resyncById(userSourceId).catch(() => {});
    };
    showToastNotification({
      type: 'error',
      title: 'Processing failed',
      description: getUserSourceErrorMessage(error, 'long') ?? 'Something went wrong while processing the source.',
      showCloseButton: true,
      ...(retryable && { action: { label: 'Resync', onClick: resync } }),
    });
  };
  const latest = useRef({ notify, openRecharge, resyncById });

  useEffect(() => {
    latest.current = { notify, openRecharge, resyncById };
  });

  return useCallback((failure: UserSourceProcessingFailure) => latest.current.notify(failure), []);
};
